import { useState } from "react";
import { motion } from "framer-motion";
import { Check, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ChapterHeading } from "@/components/ChapterHeading";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const RsvpSection = () => {
  const [name, setName] = useState("");
  const [status, setStatus] = useState("accepted");
  const [guests, setGuests] = useState("1");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) {
      if (!name.trim()) toast.error("Please tell us your name.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/rsvp", {
        name: name.trim(),
        status,
        guests: parseInt(guests, 10),
        notes: notes.trim(),
      });
      setDone(data);
      toast.success(
        data.status === "accepted"
          ? "Wonderful — you're on the list. Remember: not a word!"
          : "You'll be missed. Thank you for letting us know."
      );
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Your session expired — please re-enter the site password.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border-t border-white/10 bg-[#0A0A0A] px-6 py-24 md:px-16 md:py-36 lg:px-24" data-testid="chapter-rsvp">
      <ChapterHeading number="04" title="The" italic="RSVP" testId="chapter-rsvp-heading" />

      <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="md:col-span-5"
        >
          <p className="font-display text-2xl font-light italic leading-relaxed text-[#F5F5F0] md:text-3xl">
            "Kindly respond by the 14th of November."
          </p>
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-[#A1A19A]">
            One reply per party, please. If your plans change, simply submit the form again
            with the same name — we'll update your response. And remember: loose lips sink surprises.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="md:col-span-7"
        >
          {done ? (
            <div className="border border-gold/30 bg-[#0D0D0D] p-12" data-testid="rsvp-success">
              <PartyPopper className="mb-8 h-6 w-6 text-gold" strokeWidth={1.25} />
              <p className="font-display text-3xl font-light text-[#F5F5F0]">
                {done.status === "accepted" ? (
                  <>You're in, <em className="text-gold">{done.name}</em>.</>
                ) : (
                  <>Noted, <em className="text-gold">{done.name}</em>.</>
                )}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-[#A1A19A]">
                {done.status === "accepted"
                  ? `We've reserved a place for ${done.guests} ${done.guests === 1 ? "guest" : "guests"}. Arrive by 7:40pm — and hide well.`
                  : "Thank you for letting us know. We'll raise a glass in your honour."}
              </p>
              <button
                data-testid="rsvp-edit-button"
                onClick={() => setDone(null)}
                className="mt-8 border-b border-gold/50 pb-1 text-xs uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:border-gold hover:text-gold-hover"
              >
                Amend my response
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-[#0D0D0D] p-10 md:p-12" data-testid="rsvp-form">
              <div className="space-y-8">
                <div>
                  <Label htmlFor="rsvp-name" className="mb-3 block text-xs uppercase tracking-[0.25em] text-[#A1A19A]">
                    Your name
                  </Label>
                  <Input
                    id="rsvp-name"
                    data-testid="rsvp-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="First and last name"
                    className="h-14 rounded-none border-white/15 bg-white/5 px-5 text-base text-[#F5F5F0] placeholder:text-[#666666] focus-visible:ring-1 focus-visible:ring-gold"
                  />
                </div>

                <div>
                  <Label className="mb-4 block text-xs uppercase tracking-[0.25em] text-[#A1A19A]">
                    Will you be there?
                  </Label>
                  <RadioGroup value={status} onValueChange={setStatus} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <label
                      data-testid="rsvp-attendance-accept"
                      className={`flex cursor-pointer items-center gap-4 border p-5 transition-colors duration-300 ${
                        status === "accepted" ? "border-gold/60 bg-gold/10" : "border-white/10 hover:border-white/25"
                      }`}
                    >
                      <RadioGroupItem value="accepted" className="border-gold/60 text-gold" />
                      <span className="text-sm text-[#F5F5F0]">Joyfully accepts</span>
                    </label>
                    <label
                      data-testid="rsvp-attendance-decline"
                      className={`flex cursor-pointer items-center gap-4 border p-5 transition-colors duration-300 ${
                        status === "declined" ? "border-velvet bg-velvet/30" : "border-white/10 hover:border-white/25"
                      }`}
                    >
                      <RadioGroupItem value="declined" className="border-[#A1A19A]" />
                      <span className="text-sm text-[#F5F5F0]">Regretfully declines</span>
                    </label>
                  </RadioGroup>
                </div>

                {status === "accepted" && (
                  <div>
                    <Label className="mb-3 block text-xs uppercase tracking-[0.25em] text-[#A1A19A]">
                      Guests in your party (including you)
                    </Label>
                    <Select value={guests} onValueChange={setGuests}>
                      <SelectTrigger
                        data-testid="rsvp-guests-select"
                        className="h-14 w-40 rounded-none border-white/15 bg-white/5 text-base text-[#F5F5F0] focus:ring-1 focus:ring-gold"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none border-white/15 bg-[#111111] text-[#F5F5F0]">
                        {[1, 2, 3, 4, 5, 6].map((n) => (
                          <SelectItem key={n} value={String(n)} data-testid={`rsvp-guests-option-${n}`}>
                            {n}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label htmlFor="rsvp-notes" className="mb-3 block text-xs uppercase tracking-[0.25em] text-[#A1A19A]">
                    {status === "declined" ? "A message for Lindsay — she’ll read these after the party" : "Dietary needs or a message"}
                  </Label>
                  <Textarea
                    id="rsvp-notes"
                    data-testid="rsvp-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Allergies, vegetarian, a song request for the band…"
                    rows={4}
                    className="rounded-none border-white/15 bg-white/5 px-5 py-4 text-base text-[#F5F5F0] placeholder:text-[#666666] focus-visible:ring-1 focus-visible:ring-gold"
                  />
                </div>

                <Button
                  data-testid="rsvp-submit-button"
                  type="submit"
                  disabled={busy}
                  className="group h-14 w-full rounded-none bg-gold text-xs font-medium uppercase tracking-[0.25em] text-[#050505] transition-colors duration-300 hover:bg-gold-hover"
                >
                  {busy ? "Sending…" : "Send my RSVP"}
                  <Check className="ml-3 h-4 w-4 transition-transform duration-300 group-hover:scale-110" strokeWidth={1.5} />
                </Button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
